import { forwardRef, useMemo } from 'react'
import { visuallyHidden } from '@mui/utils'
import { AnswerBubble } from '@app/components/ui/bubbles'
import { CheckboxComponent } from '@app/components/ui/inputs'
import { STAAR_MULTIPLE_CHOICE_ANSWER } from '@app/constants'
import { percentageFormatter } from '@app/helpers'
import { getAnswerChoiceAriaLabel } from '@app/helpers/mathAccessibility'
import { MathSpeakableContext } from '@app/contexts/MathSpeakableContext'
import { BubbleWrapper, ContentWrapper, PercentageLabel, SimpleChoiceWrapper } from './styles'
import ChoiceAnswerMaskingContainer from '@app/components/ui/containers/ChoiceAnswerMaskingContainer'
import { CrossContainer } from '../../../ui/containers/CrossContainer/CrossContainer'

export interface SimpleChoiceProps {
  letterIndex?: string
  itemId: string
  identifier?: string
  fixed?: boolean
  isCorrect?: boolean
  groupMode?: boolean
  isPreviewMode?: boolean
  feedbackPercentage?: number
  feedbackFormatter?: (percentage: number) => string
  availableMultipleChoices?: boolean
  onSelect?: (id: string) => void
  selectedChoices?: string[]
  children?: JSX.Element
  accessibilityAttr?: { [key: string]: string }
  disabled?: boolean
  setFocusedChoice?: () => void
  arrowKeysNavigation?: (e: React.KeyboardEvent<Element>) => void
  isFocused?: boolean
  onChoiceFocus?: () => void
  posInSet?: number
  blankBubble?: boolean
  masking?: {
    isMasked: boolean
    isEnabled: boolean
    toggle: () => void
  }
  strikethrough?: {
    isStrikethrough: boolean
    isEnabled: boolean
    toggle: (answerId: string) => void
  }
}

const defaultFeedbackFormatter = (percentage: number) => percentageFormatter.format(percentage)

// No-op — provided so MathMLComponent can detect it is inside a choice
// (onSpeakableText !== null) and apply the correct ARIA/styling behaviour.
const CHOICE_MATH_CONTEXT = { onSpeakableText: () => {} }

export const SimpleChoiceComponent = forwardRef<HTMLDivElement, SimpleChoiceProps>((props, ref) => {
  const {
    letterIndex,
    identifier,
    availableMultipleChoices,
    isCorrect,
    groupMode = false,
    isPreviewMode = false,
    feedbackPercentage,
    feedbackFormatter = defaultFeedbackFormatter,
    onSelect,
    setFocusedChoice,
    arrowKeysNavigation,
    isFocused,
    onChoiceFocus,
    posInSet,
    selectedChoices = [],
    accessibilityAttr,
    disabled,
    blankBubble = false,
    masking,
    strikethrough,
  } = props

  const selected = useMemo(() => selectedChoices.includes(identifier), [selectedChoices, identifier])
  const isReviewMode = isCorrect !== undefined || groupMode
  const contentDescriptionId = `${identifier}-content`
  const choiceLabelId = `${identifier}-choice-aria-label`

  // aria-label carries the choice prefix and correctness status.
  // The full content (plain text + math) is read via aria-describedby below.
  const choiceAriaLabel = useMemo(() => {
    const label = letterIndex
      ? !isReviewMode || groupMode
        ? getAnswerChoiceAriaLabel(letterIndex, 'none')
        : getAnswerChoiceAriaLabel(letterIndex, isCorrect ? 'correct' : 'incorrect')
      : undefined
    return label
  }, [letterIndex, isReviewMode, groupMode, isCorrect])

  // isMasked/isStrikethrough stay true once a tool is switched off, so gate both on
  // isEnabled: off means fully inert, back on re-applies the existing selections.
  const isMasked = !!(masking?.isEnabled && masking?.isMasked)

  const eliminatedId = `${identifier}-eliminated`
  const choiceAccessibleNameIds = choiceAriaLabel
    ? `${choiceLabelId} ${contentDescriptionId}${strikethrough?.isStrikethrough ? ` ${eliminatedId}` : ''}`
    : `${contentDescriptionId}${strikethrough?.isStrikethrough ? ` ${eliminatedId}` : ''}`

  const onClickHandler = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation()
    onSelect?.(identifier)
    setFocusedChoice && setFocusedChoice()
  }

  const onKeyboardSubmitHandler = (e: React.KeyboardEvent) => {
    if (strikethrough?.isEnabled && (e.code === 'Space' || e.code === 'Enter')) {
      e.preventDefault()
      if (selected) onSelect?.(identifier)
      strikethrough.toggle(identifier)
      return
    }

    if (e.code === 'Space' || e.code === 'Enter') {
      e.preventDefault()
      onSelect?.(identifier)
    }

    !!arrowKeysNavigation && arrowKeysNavigation(e)
  }

  const renderContent = (answerMaskingOverlay?: React.ReactNode) => (
    // MathSpeakableContext signals to nested MathMLComponents that they are
    // inside a choice. Each MathMLComponent then renders with its own
    // role="math" aria-label so that aria-describedby traversal below
    // picks up each math expression in document order alongside plain text.
    <MathSpeakableContext.Provider value={CHOICE_MATH_CONTEXT}>
      <CrossContainer
        strikethrough={strikethrough}
        selected={selected}
        onSelect={onSelect}
        className={STAAR_MULTIPLE_CHOICE_ANSWER}
        id={identifier}
      >
        <SimpleChoiceWrapper
          ref={ref}
          isFocused={isFocused}
          isReviewMode={isReviewMode}
          disabled={!selected && disabled}
          isAnswerMasked={masking?.isMasked ?? false}
          isAnswerMaskingEnabled={masking?.isEnabled ?? false}
          isStrikethrough={strikethrough?.isStrikethrough}
          isStrikethroughEnabled={strikethrough?.isEnabled}
          className={STAAR_MULTIPLE_CHOICE_ANSWER}
          id={identifier}
          onFocus={onChoiceFocus}
          // Each choice is its own tab stop (Tab: A -> B -> C -> D); arrows still work too.
          tabIndex={isMasked ? -1 : 0}
          // a11y: checkbox only when >1 answer can be chosen (maxChoices > 1), else radio.
          role={availableMultipleChoices ? 'checkbox' : 'radio'}
          aria-checked={selected}
          // `disabled` goes true for every choice once maxChoices is reached, but a selected
          // answer can still be deselected — so mark them unavailable rather than unreachable.
          aria-disabled={isReviewMode || (disabled && !selected) ? true : undefined}
          aria-labelledby={choiceAccessibleNameIds}
          sx={isReviewMode ? { cursor: 'default', pointerEvents: 'none' } : undefined}
          {...(isReviewMode && !!arrowKeysNavigation ? { onKeyDown: arrowKeysNavigation } : {})}
          {...(!isMasked && !isReviewMode
            ? {
                onClick: onClickHandler,
                onKeyDown: onKeyboardSubmitHandler,
              }
            : {})}
        >
          {choiceAriaLabel && (
            <span id={choiceLabelId} style={visuallyHidden}>
              {choiceAriaLabel}
            </span>
          )}
          {strikethrough?.isStrikethrough && (
            <span id={eliminatedId} style={visuallyHidden}>
              , Eliminated.
            </span>
          )}
          {groupMode && !isPreviewMode && feedbackPercentage !== undefined && (
            <PercentageLabel correct={isCorrect} as='span'>
              {feedbackFormatter(feedbackPercentage)}
            </PercentageLabel>
          )}

          <BubbleWrapper blankBubble={blankBubble} aria-hidden={blankBubble || undefined}>
            {availableMultipleChoices && !letterIndex ? (
              <CheckboxComponent
                disabled={isMasked || (!selected && (disabled || isReviewMode))}
                checked={selected}
                correct={isCorrect}
                identifier={identifier}
                // The wrapper is the control and already carries the name and checked
                // state; hide the inner input so it is not announced a second time.
                accessibilityAttr={{ ...accessibilityAttr, 'aria-hidden': 'true' }}
                tabIndex={-1}
              />
            ) : (
              <AnswerBubble
                selected={selected}
                correct={isCorrect}
                symbol={letterIndex}
                multiple={availableMultipleChoices}
                identifier={identifier}
                accessibilityAttr={{
                  role: 'presentation',
                  'aria-label': undefined,
                  'aria-checked': undefined,
                }}
                tabIndex={-1}
                disabled={disabled || isMasked || isReviewMode}
                presentationOnly
              />
            )}
          </BubbleWrapper>

          <ContentWrapper id={contentDescriptionId} isStrikethrough={strikethrough?.isStrikethrough}>
            {props.children}
          </ContentWrapper>

          {answerMaskingOverlay}
        </SimpleChoiceWrapper>
      </CrossContainer>
    </MathSpeakableContext.Provider>
  )

  if (!masking) {
    return renderContent()
  }

  return (
    <ChoiceAnswerMaskingContainer {...masking} letterIndex={letterIndex} posInSet={posInSet}>
      {answerMaskingOverlay => renderContent(answerMaskingOverlay)}
    </ChoiceAnswerMaskingContainer>
  )
})
